import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Loader2, RefreshCw, Undo2, History } from "lucide-react";

const FIELD_LABELS = { regular_price: "Regular", sale_price: "Sale", date_on_sale_from: "Sale starts", date_on_sale_to: "Sale ends" };

export const sourceLabel = (s = "") => {
  if (s === "scheduler") return "Scheduler";
  if (s.includes("/history/")) return "Undo";
  if (s.includes("apply-prices")) return "All Sites push";
  if (s.includes("clear-expired-sales")) return "Clear expired sales";
  if (s.includes("discount-by-tag")) return "Promotion now";
  if (s.includes("/promotions")) return "Promotion";
  if (s.includes("/bulk")) return "Bulk edit";
  if (s.includes("/products/")) return "Grid edit";
  return "Edit";
};

const fmtTime = (d) => new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const fmtVal = (f, v) => (v === "" ? "—" : f.startsWith("date_") ? new Date(v).toLocaleDateString() : `$${v}`);

const ChangeList = ({ changes }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
    {Object.entries(changes).map(([f, v]) => (
      <span key={f} className="font-mono-num">
        <span className="text-muted-foreground">{FIELD_LABELS[f] || f}: </span>
        <span className="line-through text-muted-foreground">{fmtVal(f, v.old)}</span>
        <span className="mx-1">→</span>
        <span className="text-foreground">{fmtVal(f, v.new)}</span>
      </span>
    ))}
  </div>
);

const EntryRow = ({ e, onUndo, busy }) => (
  <div className="flex items-start justify-between gap-3 px-4 py-2.5 border-t border-border" data-testid={`history-entry-${e.id}`}>
    <div className="min-w-0 space-y-1">
      <div className="text-sm font-medium">
        {e.name} <span className="text-[11px] text-muted-foreground font-mono-num">{e.sku}</span>
        <span className="text-[11px] text-muted-foreground"> · {e.site_name}</span>
      </div>
      <ChangeList changes={e.changes} />
    </div>
    {e.undone_at ? (
      <Badge variant="outline" className="text-[10px] shrink-0" data-testid={`history-undone-${e.id}`}>UNDONE</Badge>
    ) : (
      <Button variant="ghost" size="sm" disabled={busy} onClick={() => onUndo(e)} data-testid={`history-undo-${e.id}`}>
        <Undo2 className="w-4 h-4" /> Undo
      </Button>
    )}
  </div>
);

const BatchCard = ({ batch, onUndo, onUndoBatch, busy }) => {
  const open = batch.entries.filter((e) => !e.undone_at).length;
  const first = batch.entries[0];
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`history-batch-${batch.id}`}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-secondary/40">
        <div className="text-sm">
          <span className="font-semibold">{fmtTime(first.created_at)}</span>
          <span className="text-muted-foreground"> · {first.actor} · </span>
          <Badge className="bg-primary/15 text-primary border border-primary/30 text-[10px]">{sourceLabel(first.source)}</Badge>
          <span className="text-muted-foreground text-xs"> · {batch.entries.length} item{batch.entries.length === 1 ? "" : "s"}</span>
        </div>
        {batch.entries.length > 1 && open > 0 && (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => onUndoBatch(batch)} data-testid={`history-undo-batch-${batch.id}`}>
            <Undo2 className="w-4 h-4" /> Undo all {open}
          </Button>
        )}
      </div>
      {batch.entries.map((e) => (
        <EntryRow key={e.id} e={e} onUndo={onUndo} busy={busy} />
      ))}
    </div>
  );
};

export default function HistoryPanel({ sites }) {
  const [entries, setEntries] = useState([]);
  const [siteId, setSiteId] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined, site_id: siteId === "all" ? undefined : siteId };
      setEntries((await api.get("/history", { params })).data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [siteId, search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const batches = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      if (!map.has(e.batch_id)) map.set(e.batch_id, { id: e.batch_id, entries: [] });
      map.get(e.batch_id).entries.push(e);
    });
    return [...map.values()];
  }, [entries]);

  const runUndo = async () => {
    const p = pending;
    setPending(null);
    setBusy(true);
    try {
      const r = p.batch
        ? await api.post("/history/undo", { ids: p.batch.entries.filter((e) => !e.undone_at).map((e) => e.id) })
        : await api.post(`/history/${p.entry.id}/undo`);
      if (r.data.undone > 0) toast.success(`Undid ${r.data.undone} change${r.data.undone === 1 ? "" : "s"}`);
      if (r.data.failed.length) toast.error(`${r.data.failed.length} could not be undone: ${r.data.failed[0].error}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const pendingCount = pending?.batch ? pending.batch.entries.filter((e) => !e.undone_at).length : 1;

  return (
    <div className="space-y-4" data-testid="history-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold flex items-center gap-2"><History className="w-5 h-5 text-primary" /> Price Change History</h2>
          <p className="text-xs text-muted-foreground">Every price change, who made it and when. Kept for 1 year.</p>
        </div>
        <div className="flex-1" />
        <Select value={siteId} onValueChange={setSiteId}>
          <SelectTrigger className="w-56" data-testid="history-site-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stores</SelectItem>
            {sites.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product or SKU" className="w-56" data-testid="history-search" />
        {busy && (
          <span className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="history-undo-progress">
            <Loader2 className="w-4 h-4 animate-spin" /> Undoing…
          </span>
        )}
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="history-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>
      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : batches.length === 0 ? (
        <p className="py-16 text-center text-muted-foreground text-sm" data-testid="history-empty">No price changes recorded yet</p>
      ) : (
        batches.map((b) => (
          <BatchCard key={b.id} batch={b} busy={busy} onUndo={(entry) => setPending({ entry })} onUndoBatch={(batch) => setPending({ batch })} />
        ))
      )}
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo {pendingCount} change{pendingCount === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              The previous prices will be written back to WooCommerce. The undo is also recorded in the history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="history-undo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runUndo} data-testid="history-undo-confirm">Undo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
